import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ExternalFlowStatus,
  ExternalConnectionError,
  ExternalConnectionParams,
} from '../interfaces/externalConnection.interface';
import { callInstallationCallback } from '../app/features/externalConnection/externalConnectionApi.service';
import useAppToast from './useAppToast';

export const useExternalConnection = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<ExternalFlowStatus>('idle');
  const [error, setError] = useState<ExternalConnectionError | null>(null);
  const { errorToast } = useAppToast();
  const initialized = useRef(false);

  // Collect ALL query params into a plain object
  const allParams: Record<string, string> = {};
  searchParams.forEach((value, key) => {
    allParams[key] = value;
  });

  console.log('[Hook] window.location.search =', window.location.search)
  console.log('[Hook] allParams =', allParams)

  // platform is the only strictly required param for our typing
  const params: ExternalConnectionParams = allParams as unknown as ExternalConnectionParams;

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    const abortController = new AbortController();

    const startFlow = async () => {
      // Validation: Only platform is required
      if (!allParams.platform || allParams.platform.trim() === '') {
        console.log('[Hook] ERROR: platform missing. allParams =', allParams)
        setError({
          code: 'MISSING_REQUIRED_PARAM',
          message: 'Missing required query parameter: platform'
        });
        setStatus('error');
        return;
      }

      setStatus('loading');

      try {
        console.log('[Hook] Calling API with allParams =', allParams)
        console.log('[Hook] Passing rawQueryString =', window.location.search)
        const response = await callInstallationCallback(
          allParams,
          abortController.signal,
          window.location.search   // raw string e.g. "?event=signup&platform=erpnext&client_id=abc%2F123=="
        );

        setStatus('redirecting');
        console.log('[Hook] API response =', response)
        console.log('[Hook] Redirecting to =', response.data.redirect_url)

        // Execute redirect exactly as returned by backend
        window.location.href = response.data.redirect_url;

      } catch (err: any) {
        if (err.name === 'AbortError') return;

        console.log('[Hook] FAILED. err =', err)
        console.log('[Hook] allParams at failure =', allParams)
        errorToast('Connection failed. Please try again or contact support.');
        window.location.href = '/auth';
        return;
      }
    };

    startFlow();

    return () => {
      abortController.abort();
    };
  }, []);

  return { status, error, params };
};
